<template>
  <div
    v-if="sortableOptions.length"
    class="table-sort d-flex d-md-none align-items-center mb-3"
    data-cy="table-sort"
  >
    <label
      :for="selectId"
      class="table-sort__label mb-0 mr-2 text-nowrap"
    >Sort by</label>

    <b-form-select
      :id="selectId"
      :value="sortBy"
      :options="options"
      class="table-sort__field custom-form-input custom-form-input--sm flex-grow-1"
      data-cy="table-sort-field"
      @change="onFieldChange"
    />

    <!-- No tooltip: this is shown at a phone's width, where a finger can't hover. `pressed` and not
         `aria-pressed`: b-button writes aria-pressed itself, and blanks it unless `pressed` is set -->
    <b-button
      type="button"
      size="sm"
      variant="outline-secondary"
      class="table-sort__direction ml-2"
      data-cy="table-sort-direction"
      aria-label="Descending"
      :pressed="sortDesc"
      :disabled="!sortBy"
      @click="$emit('update:sortDesc', !sortDesc)"
    >
      <font-awesome-icon :icon="['fas', sortDesc ? 'sort-amount-down' : 'sort-amount-down-alt']" />
    </b-button>
  </div>
</template>

<script>
import startCase from 'lodash.startcase'

/**
 * The sort a `stacked="md"` b-table loses on a phone. Below md the table is a card per row and
 * BootstrapVue hides its thead, the only sort control a b-table has. This puts the table's sortable
 * columns in a select above the cards, with a toggle for the direction, both bound with `.sync` to
 * the same `sortBy` / `sortDesc` the table uses. From md up the header is back and this is hidden.
 *
 * Picking a column starts it ascending, as a header click does, and "Default order" goes back to the
 * order the rows came in, as a click on a column that doesn't sort does (BootstrapVue's `handleSort`).
 *
 * Pass the exact `fields` the table gets, so a hidden column is never offered.
 */
export default {
  name: 'StackedTableSort',

  props: {
    fields: {
      type: Array,
      default() {
        return []
      }
    },

    sortBy: {
      type: String,
      default: ''
    },

    sortDesc: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // A field without a label is headed by its key in start case, as BootstrapVue heads it
    sortableOptions() {
      return this.fields
        .filter(field => field && field.sortable)
        .map(field => ({ value: field.key, text: field.label || startCase(field.key) }))
    },

    options() {
      return [{ value: '', text: 'Default order' }, ...this.sortableOptions]
    },

    // A page can show the same list twice (Available and Applied offers)
    selectId() {
      return `table-sort-field-${this._uid}`
    }
  },

  methods: {
    onFieldChange(key) {
      this.$emit('update:sortBy', key)
      this.$emit('update:sortDesc', false)
    }
  }
}
</script>

<style lang="scss" scoped>
// 44px targets wherever a finger is the pointer (P3.7)
@include touch-screen {
  .table-sort__field,
  .table-sort__direction {
    @include touch-target;
  }

  .table-sort__direction {
    min-width: $touch-target-size;
  }
}
</style>
